import * as React from "react";
import { Footer } from "@gc/global-components";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { CustomerNotifications } from "components/shared/CustomerNotifications";
import { CustomerNotificationLocation } from "@shared-ui/customer-notifications";

/**
 * Represents the component at the bottom of a Blossom page. It will either use
 * an HTML string (parsed to add UITK styles) from globalcontrols on mobile or the Bernie
 * footer if it is on desktop HTG/F pages
 */
export const FlexFooter = ({
  footer,
  context,
  isMobile,
}: {
  // Footer could come as a HTML string or JSON object
  footer: string | object;
  context: ExtendedContextStore;
  isMobile: boolean;
}) => {
  const pageType = context.searchContext.pageType;

  if (pageTypesWithNoFooter.includes(pageType)) {
    return null;
  }

  const isBexStorefrontPage =
    context.searchContext.pageType.indexOf("Storefront") > -1 && context.site.brand === "expedia";
  const largeScreenWhiteListedPageTypesForBernieFooter =
    !isMobile && whiteListedPageTypesForBernieFooter.includes(pageType);
  const isHotwireBrand = context.site.brand === "hotwire";
  const isCometStorefrontPage =
    context.searchContext.pageType.indexOf("Storefront") > -1 &&
    (context.site.brand === "orbitz" || context.site.brand === "travelocity");

  // Use the bernie footer for desktop HTG/F pages, DX pages, or as a fallback for other pages.
  // For desktop HTG/F pages, SWXL links are into their own modules.
  // For the Hotwire brand, always use the bernie footer, even on mobile.
  const shouldUseBernieFooter =
    largeScreenWhiteListedPageTypesForBernieFooter || isHotwireBrand || !footer || typeof footer === "object";

  const marqueeClass = isBexStorefrontPage
    ? "bex-homepage-module"
    : isCometStorefrontPage
    ? "comet-homepage-module"
    : "";

  if (shouldUseBernieFooter) {
    return (
      <div className="all-y-padding-three">
        <Footer context={context} marqueeClass={marqueeClass} />
      </div>
    );
  }

  return (
    <>
      <div className="SimpleContainer">
        <div>
          <CustomerNotifications location={CustomerNotificationLocation.FOOTER} className={"all-y-margin-six"} />
        </div>
      </div>
      <footer
        className="TripPlanning uitk-cell all-x-padding-three all-cell-1-1 footer"
        dangerouslySetInnerHTML={createMarkup(footer as string)} //Typescript doesn't understand the typeof check above.
      />
    </>
  );
};

/**
 * Replaces parts of a string footer HTML to add styling. Should eventually be
 * deprecated in favor of a Bernie footer.
 * @param footer - String representing an HTML markup from Flex to parse through
 */
function createMarkup(footer: string) {
  const footerWithStyle = footer
    ? footer
        .replace(/<h5/g, `<h5 class="uitk-type-heading-300"`)
        .replace(/class="flag/g, 'class="uitk-flag flag')
        // In UITK, the UK flag has a "gb" class so we have to change it
        .replace(/flag uk/g, "flag gb")
        // In Uitk the Indonesia flag has a "id" class so we have to change it
        .replace(/flag ind/g, "flag id")
    : footer;

  return { __html: footerWithStyle };
}

const whiteListedPageTypesForBernieFooter = [
  "Travel-Guide-Hotels",
  "Travel-Guide-Filter-Hotels",
  "Travel-Guide-Accommodation",
  "Destination-Discovery",
  "Neighborhood-Discovery",
  "Destination-Discovery-Where-To-Stay",
  "Hotel-Reviews",
  "Destination-Travel-Guides",
  "Merch-Campaign",
];

const pageTypesWithNoFooter = ["Html-Sitemap"];
